import React from 'react'
import type { ChartFrameProps } from './ChartFrame.types'
import './ChartFrame.css'

const ChartFrame: React.FC<ChartFrameProps> = ({
  children,
  xLabels,
  yTicks,
  xAxisLabel,
  yAxisLabel,
  showXAxisLabel = true,
  showYAxisLabel = true,
  legend,
  legendPosition = 'bottom',
  showGrid,
  plotHeight,
  darkMode = false,
  className = '',
  style,
}) => {
  const hasYTicks = Boolean(yTicks && yTicks.length > 0)
  const hasXLabels = Boolean(xLabels && xLabels.length > 0)
  const hasLegend = Boolean(legend && legend.length > 0)
  const gridEnabled = showGrid ?? hasYTicks
  const showXTitle = Boolean(xAxisLabel) && showXAxisLabel
  const showYTitle = Boolean(yAxisLabel) && showYAxisLabel

  const classNames = [
    'ds-chart-frame',
    darkMode ? 'ds-chart-frame--dark' : '',
    !showYTitle ? 'ds-chart-frame--no-y-title' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ')

  const legendList =
    hasLegend && legend ? (
      <ul className="ds-chart-frame__legend" aria-label="Legend">
        {legend.map((item) => (
          <li key={item.label} className="ds-chart-frame__legend-item">
            <span
              className="ds-chart-frame__legend-swatch"
              style={{ background: item.color }}
              aria-hidden="true"
            />
            <span>{item.label}</span>
          </li>
        ))}
      </ul>
    ) : null

  const plotStyle =
    plotHeight != null
      ? ({ minHeight: `${plotHeight}px`, height: `${plotHeight}px` } as const)
      : undefined

  return (
    <div className={classNames} style={style}>
      {legendPosition === 'top' ? legendList : null}

      <div className="ds-chart-frame__body">
        {showYTitle ? (
          <p className="ds-chart-frame__y-title">{yAxisLabel}</p>
        ) : null}

        {hasYTicks ? (
          <ul className="ds-chart-frame__y-ticks" aria-hidden="true">
            {yTicks!.map((tick, index) => (
              <li key={`${tick}-${index}`} className="ds-chart-frame__y-tick">
                {tick}
              </li>
            ))}
          </ul>
        ) : (
          <span className="ds-chart-frame__y-ticks" aria-hidden="true" />
        )}

        <div className="ds-chart-frame__plot" style={plotStyle}>
          {gridEnabled && hasYTicks ? (
            <div className="ds-chart-frame__grid" aria-hidden="true">
              {yTicks!.map((tick, index) => (
                <span
                  key={`grid-${tick}-${index}`}
                  className="ds-chart-frame__grid-line"
                />
              ))}
            </div>
          ) : null}
          <div className="ds-chart-frame__plot-content">{children}</div>
        </div>

        {hasXLabels ? (
          <ol
            className="ds-chart-frame__x-labels"
            style={{
              gridTemplateColumns: `repeat(${xLabels!.length}, minmax(0, 1fr))`,
            }}
            aria-hidden="true"
          >
            {xLabels!.map((label) => (
              <li key={label} className="ds-chart-frame__x-label">
                {label}
              </li>
            ))}
          </ol>
        ) : null}

        {showXTitle ? (
          <p className="ds-chart-frame__x-title">{xAxisLabel}</p>
        ) : null}
      </div>

      {legendPosition === 'bottom' ? legendList : null}
    </div>
  )
}

export default ChartFrame
